import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, HelpCircle } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLang } from "@/contexts/LanguageContext";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const StarCrescent = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M15 4a8 8 0 100 16 6.5 6.5 0 010-13 6.5 6.5 0 000-3z"
      fill="#E30A17"
    />
    <path
      d="M17.3 10.4l1.7-1.3-2.1-.2-.8-2-.8 2-2.1.2 1.6 1.3-.5 2 1.8-1.1 1.8 1.1-.6-2z"
      fill="#fff"
    />
  </svg>
);

const TurkishFlag = ({ size = 28 }) => (
  <svg
    width={size}
    height={Math.round(size * 0.66)}
    viewBox="0 0 300 200"
    aria-hidden
    className="rounded-[2px] shadow-[0_2px_8px_rgba(227,10,23,0.35)]"
  >
    <rect width="300" height="200" fill="#E30A17" />
    <circle cx="120" cy="100" r="45" fill="#FFFFFF" />
    <circle cx="132" cy="100" r="36" fill="#E30A17" />
    <polygon
      points="188,100 200,82 220,82 205,96 212,116 188,104 164,116 171,96 156,82 176,82"
      fill="#FFFFFF"
    />
  </svg>
);

const FootballIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
    className="shrink-0"
  >
    <circle cx="12" cy="12" r="9.2" fill="#ffffff" stroke="#0a0a0a" strokeWidth="0.8" />
    <path
      d="M12 5.4l2.5 1.8-0.95 2.95h-3.1L9.5 7.2 12 5.4z M5.4 11.3l2.8-0.9 2 2.7-1 3-2.8 0.05L4.9 13.4l0.5-2.1z M18.6 11.3l-0.5 2.1 -1.5 2.75-2.8-0.05-1-3 2-2.7 3.8 0.9z M9.1 18.3l-1.55-2.1 1-3 3.1 0 1 3-1.55 2.1 -2 0z"
      fill="#0a0a0a"
    />
  </svg>
);

const OneXTwoBadge = () => (
  <div
    aria-hidden
    className="inline-flex items-center gap-0.5 border border-white/15 bg-black/40 backdrop-blur-md p-0.5"
    data-testid="onex2-badge"
  >
    <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 bg-[var(--hk-red)] text-white text-[10px] font-bold tracking-[0.05em]">
      1
    </span>
    <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 text-white/85 text-[10px] font-bold tracking-[0.05em]">
      X
    </span>
    <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 bg-[var(--hk-red)] text-white text-[10px] font-bold tracking-[0.05em]">
      2
    </span>
  </div>
);

const Header = () => {
  const { t } = useLang();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const goTo = (id) => {
    if (!isHome) {
      window.location.href = "/#" + id;
    } else {
      scrollTo(id);
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled || open
          ? "bg-black/75 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="container-hk flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <Link
          to="/"
          data-testid="brand-logo"
          onClick={(e) => {
            if (location.pathname === "/") {
              e.preventDefault();
            }
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center gap-2 md:gap-3 group"
        >
          <StarCrescent />
          <span className="font-display text-lg md:text-xl tracking-[0.15em] text-white">
            HAKAN<span className="text-[var(--hk-red)]">.</span>TIPS
          </span>
          <span
            data-testid="brand-flag"
            className="ml-1 md:ml-2 inline-flex"
            aria-label="Türkiye"
          >
            <TurkishFlag size={26} />
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
          <button
            data-testid="nav-home"
            onClick={() => goTo("home")}
            className="text-[11px] font-semibold tracking-[0.25em] text-white/80 hover:text-white transition-colors"
          >
            {t.nav.home}
          </button>
          <button
            data-testid="nav-plans"
            onClick={() => goTo("plans")}
            className="text-[11px] font-semibold tracking-[0.25em] text-white/80 hover:text-white transition-colors"
          >
            {t.nav.plans}
          </button>
          <button
            data-testid="nav-free-tip"
            onClick={() => goTo("free-tip")}
            className="text-[11px] font-semibold tracking-[0.25em] text-[var(--hk-red)] hover:text-white transition-colors"
          >
            {t.nav.free_tip}
          </button>
          <button
            data-testid="nav-archives"
            onClick={() => goTo("archive")}
            className="text-[11px] font-semibold tracking-[0.25em] text-white/80 hover:text-white transition-colors"
          >
            {t.nav.doubles_archives}
          </button>
          <button
            data-testid="nav-about"
            onClick={() => goTo("about-hakan")}
            className="text-[11px] font-semibold tracking-[0.25em] text-white/80 hover:text-white transition-colors"
          >
            {t.nav.about}
          </button>
          <button
            data-testid="nav-contact"
            onClick={() => goTo("contact")}
            className="text-[11px] font-semibold tracking-[0.25em] text-white/80 hover:text-white transition-colors"
          >
            {t.nav.contact}
          </button>
          <LanguageSwitcher />
        </nav>

        {/* Right side */}
        <div className="hidden lg:flex items-center gap-3 shrink-0">
          <button
            data-testid="nav-help"
            onClick={() => goTo("contact")}
            className="flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.25em] text-white/70 hover:text-white transition-colors whitespace-nowrap"
          >
            <HelpCircle size={14} />
            {t.nav.help}
          </button>
          <button
            data-testid="cta-buy-header"
            onClick={() => goTo("plans")}
            className="btn-red px-5 py-3 text-[11px] font-bold tracking-[0.25em] whitespace-nowrap"
          >
            {t.nav.buy} →
          </button>
        </div>

        {/* Mobile toggle */}
        <div className="flex lg:hidden items-center gap-2">
          <LanguageSwitcher compact />
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            className="p-2 border border-white/15 text-white"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden overflow-hidden bg-black border-b border-white/10"
          >
            <div className="container-hk py-6 flex flex-col gap-4">
              <button
                onClick={() => goTo("home")}
                className="text-left text-sm font-semibold tracking-[0.2em] text-white py-3 border-b border-white/5"
                data-testid="m-nav-home"
              >
                {t.nav.home}
              </button>
              <button
                onClick={() => goTo("plans")}
                className="text-left text-sm font-semibold tracking-[0.2em] text-white py-3 border-b border-white/5"
                data-testid="m-nav-plans"
              >
                {t.nav.plans}
              </button>
              <button
                onClick={() => goTo("free-tip")}
                className="text-left text-sm font-semibold tracking-[0.2em] text-[var(--hk-red)] py-3 border-b border-white/5"
                data-testid="m-nav-free-tip"
              >
                {t.nav.free_tip}
              </button>
              <button
                onClick={() => goTo("archive")}
                className="text-left text-sm font-semibold tracking-[0.2em] text-white py-3 border-b border-white/5"
                data-testid="m-nav-archives"
              >
                {t.nav.doubles_archives}
              </button>
              <button
                onClick={() => goTo("about-hakan")}
                className="text-left text-sm font-semibold tracking-[0.2em] text-white py-3 border-b border-white/5"
                data-testid="m-nav-about"
              >
                {t.nav.about}
              </button>
              <button
                onClick={() => goTo("contact")}
                className="text-left text-sm font-semibold tracking-[0.2em] text-white py-3 border-b border-white/5"
                data-testid="m-nav-contact"
              >
                {t.nav.contact}
              </button>
              <button
                onClick={() => goTo("contact")}
                className="text-left text-sm font-semibold tracking-[0.2em] text-white/70 py-3 border-b border-white/5"
                data-testid="m-nav-help"
              >
                {t.nav.help}
              </button>
              <button
                data-testid="cta-buy-mobile"
                onClick={() => goTo("plans")}
                className="btn-red mt-2 py-4 text-sm font-bold tracking-[0.25em]"
              >
                {t.nav.buy} →
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
